import React, { useEffect, useRef, useState } from "react";
import { Timer } from "lucide-react";

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const ExamTimer = ({
  startTime,
  totalTimeSeconds,
  onTimeUp,
  timeRemaining: initialTimeRemaining,
  onTimeUpdate,
}) => {
  const [currentTime, setCurrentTime] = useState(
    initialTimeRemaining || totalTimeSeconds
  );
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    // Set the actual start time reference
    if (startTime) {
      startTimeRef.current = startTime;
    } else if (initialTimeRemaining) {
      // If we have remaining time, calculate when the timer should have started
      const elapsedTime = totalTimeSeconds - initialTimeRemaining;
      startTimeRef.current = Date.now() - elapsedTime * 1000;
    }

    setCurrentTime(initialTimeRemaining || totalTimeSeconds);
  }, [startTime, totalTimeSeconds, initialTimeRemaining]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTimeRef.current) / 1000);
      const remaining = Math.max(0, totalTimeSeconds - elapsed);

      setCurrentTime(remaining);

      if (onTimeUpdate) {
        onTimeUpdate(remaining);
      }

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        onTimeUp();
        return;
      }
    };

    // Start the timer immediately
    updateTimer();

    // Then update every second
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [totalTimeSeconds, onTimeUp, onTimeUpdate]);

  // Color coding based on remaining time percentage
  const getTimerColor = () => {
    if (!totalTimeSeconds || currentTime <= 0) return "text-red-500";

    const percentage = (currentTime / totalTimeSeconds) * 100;
    if (percentage <= 5) return "text-red-500";
    if (percentage <= 10) return "text-orange-500";
    if (percentage <= 20) return "text-yellow-500";
    return "text-primary";
  };

  const getBackgroundColor = () => {
    if (!totalTimeSeconds || currentTime <= 0) return "bg-red-50";

    const percentage = (currentTime / totalTimeSeconds) * 100;
    if (percentage <= 5) return "bg-red-50";
    if (percentage <= 10) return "bg-orange-50";
    if (percentage <= 20) return "bg-yellow-50";
    return "bg-gray-100";
  };

  return (
    <div
      className={`flex items-center space-x-2 ${getBackgroundColor()} px-4 py-2 rounded-full border-2 ${
        currentTime <= 0
          ? "border-red-300"
          : (currentTime / totalTimeSeconds) * 100 <= 5
          ? "border-red-300"
          : (currentTime / totalTimeSeconds) * 100 <= 10
          ? "border-orange-300"
          : (currentTime / totalTimeSeconds) * 100 <= 20
          ? "border-yellow-300"
          : "border-gray-300"
      }`}
    >
      <Timer
        className={`h-5 w-5 ${getTimerColor()} ${
          currentTime <= 0 ? "animate-pulse" : ""
        }`}
      />
      <span className={`font-medium text-lg ${getTimerColor()}`}>
        {formatTime(currentTime)}
      </span>
      {currentTime <= totalTimeSeconds * 0.05 && currentTime > 0 && (
        <span className="text-xs text-red-500 animate-pulse">
          Time Running Out!
        </span>
      )}
    </div>
  );
};

export default ExamTimer;
